import { RouteConstant } from 'constants/common';
import { IBriefFilterModel, IBriefModel } from 'services/brief/types';
import { IBriefDetailsResponse, ICampaignAssignResponse, IDigitalAssetsResponse, PoRequiredType } from 'services/brief/types.response';
import { ICampaignPlanItemResponse } from 'services/campaign/types';
import { ICampaignSetupResponse } from 'services/campaign/types.response';
import { FormListEnum } from 'services/master/types.form';
import { Status } from 'services/master/types.status';
import { IFilterData } from './brief-filter';
import { IObjectiveCard, IObjectiveRow } from './objective/types';

export const PartOneWorkflowStatuses = [Status.PendingPlApproval, Status.Part1Approved, Status.Part1Rejected];

export enum PageAction {
    Edit = 'edit',
    Copy = 'copy',
    View = 'view',
}

export const isNoPoRequired = (data: IBriefDetailsResponse) => {
    return data.contact?.poRequired === PoRequiredType.NoPORequired;
};
export const getBriefStatus = (row: IBriefModel) => {
    if (
        [Status.CampaignLive, Status.CampaignEnded, Status.Draft, Status.InActive, ...PartOneWorkflowStatuses].includes(row.statusId)
    ) {
        return row.statusDescription;
    } else if (Status.Delayed === row.overallStatusId) {
        return 'Delayed';
    }
    return 'In Progress';
};

// Short label for the Progress pill - the full DB description overflows the fixed width.
// The full text still shows in "Last Event" via getBriefStatus.
export const getBriefProgressLabel = (row: IBriefModel) => {
    switch (row.statusId) {
        case Status.PendingPlApproval:
            return 'Pending PL Approval';
        case Status.Part1Approved:
            return 'PL Approved';
        case Status.Part1Rejected:
            return 'PL Rejected';
        default:
            return getBriefStatus(row);
    }
};

export const getBriefNavigateUrl = (row: IBriefModel, isPerformanceLead?: boolean) => {
    const briefId = row.briefId!.toString();
    if (row.statusId === Status.Draft || row.statusId === Status.InActive) {
        return RouteConstant.getEditBriefUrl(briefId);
    }
    // Pending/rejected opens the approval panel on brief-detail, for Planner and PL alike.
    if (row.statusId === Status.PendingPlApproval || row.statusId === Status.Part1Rejected) {
        return RouteConstant.getEditBriefUrl(briefId);
    }
    // Once approved the PL moves on to In Platform QA on more-info; the Planner still needs
    // brief-detail to complete Part 2, so the split applies to the PL only.
    if (row.statusId === Status.Part1Approved && !isPerformanceLead) {
        return RouteConstant.getEditBriefUrl(briefId);
    }
    return RouteConstant.getMoreInfoUrl(row.formListId ?? 0, row.briefId!);
};

type BudgetKeys = keyof Pick<IObjectiveRow, 'adServing' | 'brandSafety' | 'budget' | 'platformAgencyFee' | 'sa360'|'lotame'>;
export function getSumOfObjectiveBudget(data: IObjectiveCard[], key: BudgetKeys = 'budget') {
    let total = data
        .filter((fItem) => !fItem.isDeleted)
        .reduce((prv, item) => {
            return (
                prv +
                item.objectiveRows
                    .filter((item) => !item.isDeleted)
                    .reduce((p1, i1) => {
                        return p1 + Number(i1[key] || 0);
                    }, 0)
            );
        }, 0);
    return Number(total.toFixed(4));
}
export function toBriefFilter(filter: IFilterData, agencyId?: number): IBriefFilterModel | undefined {
    if (filter.isSearch) {
        if (filter.searchBy?.value && filter.searchByVal) {
            return {
                searchBy: filter.searchBy?.value,
                searchByVal: filter.searchByVal,
            };
        }
    } else {
        const fil: IBriefFilterModel = {
            agencyId: agencyId ?? filter.agency?.value ?? 0,
            brandId: filter.brand?.value ?? 0,
            clientId: filter.client?.value ?? 0,
            statusId: filter.status?.value ?? 0,
            progressId: filter.progress?.value ?? 0,
            platformId: filter.platform?.value ?? 0,
            campaignManagerId: filter.campaignManager?.value ? filter.campaignManager?.value : undefined,
            eventId: filter.event?.value ?? 0,
            startDate: filter.dateRange ? filter.dateRange.start : null,
            endDate: filter.dateRange ? filter.dateRange.end : null,
            strategistId: filter.Strategist?.value ? filter.Strategist?.value : undefined,
        };
        return fil;
    }
}
export const getCampaignPlan = (
    data: IBriefDetailsResponse,
    formId: FormListEnum,
    platformId?: number,
): ICampaignPlanItemResponse | undefined => {
    let plans = data?.campaignDetail?.campaignPlan?.campaignPlanItems || [];
    let plan = plans.filter((a: any) => {
        return a.formListId === formId && (!platformId || a.platformId === platformId);
    })[0];
    return plan;
};

export const getSelectedBudgetType = (isMonthly: boolean) => {
    const val = isMonthly ? 'Yes' : 'No';
    return getBudgetTypeDdl().filter((a) => a.value === val)[0];
};

export const getBudgetTypeDdl = () => {
    return [
        {
            label: 'The total campaign budget divided by the number of months.',
            value: 'No',
        },
        {
            label: 'Different budget per month ',
            value: 'Yes',
        },
    ];
};

export function getClonedBriefDetails(data: IBriefDetailsResponse) {
    data = removeDeletedItemsFromBriefDetails(data);
    let val: IBriefDetailsResponse = {
        ...data,
        id: undefined as any,
        statusId: Status.Draft,
        contact: { ...data.contact!, id: undefined as any, fileUpload: undefined },
        status: {
            id: Status.Draft,
            name: 'Draft',
        },
        campaignDetail: data.campaignDetail
            ? {
                  ...data.campaignDetail,
                  id: undefined as any,
                  campaignName: `${data.campaignDetail.campaignName} - copy`,
                  campaignMonthlyBugets: data.campaignDetail?.campaignMonthlyBugets?.map((a) => ({ ...a, id: undefined as any })),
              }
            : undefined,
        targettingInstruction: data.targettingInstruction?.map((a) => ({ ...a, id: undefined as any })),
        spentByObjectives: data.spentByObjectives?.map((a) => ({
            ...a,
            id: undefined as any,
            spentByObjectiveAndBudgets: a.spentByObjectiveAndBudgets.map((a) => ({ ...a, id: undefined as any })),
        })),
    };
    return val;
}

export function removeDeletedItemsFromBriefDetails(data: IBriefDetailsResponse) {
    let val: IBriefDetailsResponse = {
        ...data,
        campaignDetail: data.campaignDetail
            ? {
                  ...data.campaignDetail,
                  campaignMonthlyBugets: data.campaignDetail?.campaignMonthlyBugets?.filter((a) => !a.isDeleted),
              }
            : undefined,
        targettingInstruction: data.targettingInstruction?.filter((a) => !a.isDeleted),
        campaignAssigns: data.campaignAssigns?.filter((a) => !a.isDeleted),
        spentByObjectives: data.spentByObjectives
            ?.filter((a) => !a.isDeleted)
            .map((a) => ({
                ...a,
                spentByObjectiveAndBudgets: a.spentByObjectiveAndBudgets.filter((f) => !f.isDeleted),
            })),
        campaignSetups: data.campaignSetups?.filter((a) => !a.isDeleted),
        //digitalAssets: data.digitalAssets?.filter((a) => !a.isDeleted),
        //     .map((a) => ({ ...a, digitalAssetsHistory: a.digitalAssetsHistory.filter((f) => !f.isDeleted) })),
        digitalAssets: data.digitalAssets
            ?.filter((a) => !a.isDeleted)
            .map((a) => ({
                ...a,
                digitalAssetsHistory: a.digitalAssetsHistory?.filter((f) => !f.isDeleted),
            })),
    };
    return val;
}

export function getAssignedPlatfroms(
    platformId: number,
    campaignAssigns: ICampaignAssignResponse[],
    userId: string,
    isGlobalAdmin: boolean,
    isAGadmin: boolean,
) {
    return (campaignAssigns.filter((a) => a.platformId === platformId && (a.assignedUserId === userId || isGlobalAdmin || isAGadmin)) ?? [])[0];
}

export function isAllCampaignAssigned(data: IBriefDetailsResponse) {
    let plCount = data.spentByObjectives?.length ?? 0;
    let assigned = data.spentByObjectives?.filter((o) => {
        let assignedUser = data.campaignAssigns?.filter(
            (a) =>
                a.assignedUserId &&
                o.platform.id === a.platformId &&
                (a?.statusId === Status.AssignToCampaignManager || a?.statusId === Status.Assign),
        )[0]?.assignedUserId;
        return !!assignedUser;
    });
    return plCount === assigned?.length;
}

export function isAllPlatformAssigned(data: IBriefDetailsResponse) {
    let assigned = data.spentByObjectives?.filter((o) => {
        let assignedUser = data.campaignAssigns?.filter((a) => a.assignedUser && o.platform.id === a.platformId)[0]?.assignedUser;
        return !!assignedUser;
    });
    return assigned?.length === data.spentByObjectives?.length;
}

export function getCompletedCampaignSetup(platformId: number, campaignSetups?: ICampaignSetupResponse[]) {
    let all = campaignSetups?.filter((a) => a.platformId === platformId && a.setupStatusId === Status.Approved) ?? [];
    return all;
}
export function getCompletedDigitalAssets(platformId: number, assets?: IDigitalAssetsResponse[]) {
    let all = assets?.filter((a) => a.platformId === platformId && a.statusId === Status.Approved) ?? [];
    return all;
}
export function isMyCampaignSetupCompleted(data: IBriefDetailsResponse, userId: string, isGlobalAdmin: boolean) {
    let myCount = data.campaignAssigns?.filter((a) => a.assignedUserId === userId || isGlobalAdmin).map((p) => p.platformId);
    return (
        (myCount?.length ?? 0) ===
        data.campaignSetups?.filter(
            (a) => [Status.Approved, Status.SubmitForReviewInternally].includes(a.setupStatusId!) && myCount?.includes(a.platformId),
        ).length
    );
}
export function isAllCampaignSetupCompleted(data: IBriefDetailsResponse) {
    let plCount = data.spentByObjectives?.length ?? 0;
    return (
        plCount ===
        data.campaignSetups?.filter((a) => [Status.Approved, Status.SubmitForReviewInternally].includes(a.setupStatusId!)).length
    );
}

export function getAnyCompletedCampaignSetup(data: IBriefDetailsResponse) {
    return data.campaignSetups?.filter((a) => a.setupStatusId === Status.Approved);
}

export function isCampaignSetupSendToAgencyCompleted(data: IBriefDetailsResponse, platformId: number, objectiveId?: number) {
    return (
        (data.campaignSetups?.filter(
            (a) =>
                a.platformId === platformId &&
                a.pObjectiveId === objectiveId &&
                a.agencyApprovalStatusId &&
                [Status.Approved, Status.SentToAgencyForApproval].includes(a.agencyApprovalStatusId),
        ).length ?? 0) > 0
    );
}
export function getApprovedOrRejectedCampaignByAgency(data: IBriefDetailsResponse, platformId: number, objectiveId: number) {
    // return (
    //     data.campaignSetups?.filter(
    //         (a) =>
    //             a.platformId === platformId &&
    //             a.agencyApprovalStatusId &&
    //             [Status.Approved, Status.Rejected].includes(a.agencyApprovalStatusId),
    //     ) ?? []
    // );
    return (
        data.campaignSetups?.filter(
            (a) =>
                a.platformId === platformId &&
                a.pObjectiveId === objectiveId && 
                a.agencyApprovalStatusId === Status.Approved,
        ) ?? []
    );
}
export function getAllApprovedCampaignByAgency(data: IBriefDetailsResponse, platformId?: number, pObjectiveId?: number) {
    return (
        data.campaignSetups?.filter(
            (a) =>
                a.agencyApprovalStatusId &&
                (a.platformId === platformId || !platformId) &&
                [Status.Approved].includes(a.agencyApprovalStatusId),
        ) ?? []
    );
}
export function getAllApprovedObjectivesCampaignByAgency(data: IBriefDetailsResponse, platformId?: number, pObjectiveId?: number) {
    return (
        data.campaignSetups?.filter(
            (a) =>
                a.agencyApprovalStatusId &&
                (a.platformId === platformId || !platformId) &&
                (a.pObjectiveId === pObjectiveId || !pObjectiveId) &&
                [Status.Approved].includes(a.agencyApprovalStatusId),
        ) ?? []
    );
}
// export function isAllCampaignStatusCompletedByAgency(data: IBriefDetailsResponse) {
//     let plCount = data.spentByObjectives?.length ?? 0;
//     return (
//         plCount ===
//         data.campaignSetups?.filter((a) => a.agencyApprovalStatusId && [Status.Approved].includes(a.agencyApprovalStatusId)).length
//     );
// }
// export function isAllCampaignStatusCompletedByAgency(data: IBriefDetailsResponse) {
//     const totalObjectiveCount = data.spentByObjectives?.reduce((acc, platform) => {
//         const activeObjectives = platform.spentByObjectiveAndBudgets?.filter(o => !o.isDeleted).length ?? 0;
//         return acc + activeObjectives;
//     }, 0) ?? 0;

//     const approvedCount = data.campaignSetups?.filter(
//         (a) => a.agencyApprovalStatusId === Status.Approved
//     ).length ?? 0;

//     return totalObjectiveCount > 0 && totalObjectiveCount === approvedCount;
// }
export function isAllCampaignStatusCompletedByAgency(data: IBriefDetailsResponse) {
    const isCreatedBefore202603 = isBriefCreatedBefore20260314(data);

    if (isCreatedBefore202603) {
        // Legacy logic: no objectiveId check, just match platform-level count
           let plCount = data.spentByObjectives?.length ?? 0;
    return (
        plCount ===
        data.campaignSetups?.filter((a) => a.agencyApprovalStatusId && [Status.Approved].includes(a.agencyApprovalStatusId)).length
    );
    }

    // New logic: match by platformId + objectiveId — ALL must be approved
    return (
        (data.spentByObjectives?.length ?? 0) > 0 &&
        data.spentByObjectives?.every((c) =>
            c.spentByObjectiveAndBudgets?.filter(o => !o.isDeleted).every((obj) => {
                return data.campaignSetups?.some(
                    (a) =>
                        a.platformId === c.platform.id &&
                        a.pObjectiveId === obj.platformObjective?.id &&
                        a.agencyApprovalStatusId === Status.Approved,
                );
            }),
        )
    );
}
export const PerformanceLeadAnswer = { yes: 'Yes', no: 'No' };
export const getPerformanceLeadDdl = () => [
    { label: 'Yes', value: PerformanceLeadAnswer.yes },
    { label: 'No', value: PerformanceLeadAnswer.no },
];
export const getSelectedPerformanceLeadOption = (flag?: boolean | null) => {
    if (flag === true) return getPerformanceLeadDdl()[0];
    if (flag === false) return getPerformanceLeadDdl()[1];
    return undefined; // legacy brief - leave the picker blank rather than implying a choice
};
export const getBriefLastEventStatus = (row: IBriefModel) => {
    if (row.statusId !== Status.SentToAgencyForApproval || row.isPerformanceLead == null) {
        return row.statusDescription;
    }
    return row.isPerformanceLead ? 'Sent to PL review in QA' : 'Sent to Buddy QA';
};
export enum QaReviewerKind {
    None = 'none',
    PerformanceLead = 'pl',
    Buddy = 'buddy',
}

export function getQaReviewerKind(data: IBriefDetailsResponse): QaReviewerKind {
    const flag = data.contact?.isPerformanceLead;
    if (flag === true) {
        return QaReviewerKind.PerformanceLead;
    }
    if (flag === false) {
        return QaReviewerKind.Buddy;
    }
    if (!!data.partOneApproval || !!data.campaignAssigns?.some((a) => !!a.assignedPerformanceLead)) {
        return QaReviewerKind.PerformanceLead;
    }
    return QaReviewerKind.None;
}
export function hasAnyPerformanceLead(data: IBriefDetailsResponse) {
    return getQaReviewerKind(data) === QaReviewerKind.PerformanceLead;
}
export function isBuddyBrief(data: IBriefDetailsResponse) {
    return getQaReviewerKind(data) === QaReviewerKind.Buddy;
}
export function getBuddyNameForPlatform(data: IBriefDetailsResponse, platformId?: number) {
    if (!isBuddyBrief(data)) {
        return undefined;
    }
    const buddy = data.campaignAssigns?.find((a) => a.platformId === platformId && !!a.assignedBuddy)?.assignedBuddy;
    return buddy ? `${buddy.firstName} ${buddy.lastName}` : undefined;
}
export function getQaReviewerNameForPlatform(data: IBriefDetailsResponse, platformId?: number) {
    const kind = getQaReviewerKind(data);
    if (kind === QaReviewerKind.Buddy) {
        return getBuddyNameForPlatform(data, platformId);
    }
    if (kind === QaReviewerKind.PerformanceLead) {
        const lead = data.campaignAssigns?.find((a) => a.platformId === platformId && !!a.assignedPerformanceLead)?.assignedPerformanceLead;
        return lead ? `${lead.firstName} ${lead.lastName}` : data.partOneApproval?.performanceLeadName || undefined;
    }
    return undefined;
}
export function hasQaStepForPlatform(data: IBriefDetailsResponse, platformId?: number) {
    return getQaReviewerKind(data) !== QaReviewerKind.None;
}
export function hasPerformanceLeadForPlatform(data: IBriefDetailsResponse, platformId?: number) {
    return hasAnyPerformanceLead(data);
}
export function isAwaitingQaReview(data: IBriefDetailsResponse) {
    const submitted = data.campaignSetups?.filter((a) => a.agencyApprovalStatusId === Status.SentToAgencyForApproval) ?? [];
    if (submitted.length === 0) {
        return false;
    }
    return submitted.every((a) => hasQaStepForPlatform(data, a.platformId) && a.plApprovalStatusId !== Status.Approved);
}
export function hasAssignedQaReviewer(data: IBriefDetailsResponse, platformId?: number) {
    const kind = getQaReviewerKind(data);
    if (kind === QaReviewerKind.Buddy) {
        return !!data.campaignAssigns?.some((a) => a.platformId === platformId && !!a.assignedBuddy);
    }
    if (kind === QaReviewerKind.PerformanceLead) {
        return (
            !!data.campaignAssigns?.some((a) => a.platformId === platformId && !!a.assignedPerformanceLead) ||
            !!data.partOneApproval?.performanceLeadId
        );
    }
    return false;
}
// Mirrors isAllCampaignStatusCompletedByAgency for the PL. No legacy branch needed - a brief
// old enough to need one never has a Performance Lead.
export function isAllCampaignStatusCompletedByPl(data: IBriefDetailsResponse) {
    return (
        (data.spentByObjectives?.length ?? 0) > 0 &&
        data.spentByObjectives?.every((c) =>
            c.spentByObjectiveAndBudgets
                ?.filter((o) => !o.isDeleted)
                .every((obj) => {
                    return data.campaignSetups?.some(
                        (a) =>
                            a.platformId === c.platform.id &&
                            a.pObjectiveId === obj.platformObjective?.id &&
                            a.plApprovalStatusId === Status.Approved,
                    );
                }),
        )
    );
}
// export function isAllCampaignSetupSendToAgencyCompleted(data: IBriefDetailsResponse) {
//     let plCount = data.spentByObjectives?.length ?? 0;
//     return (
//         plCount ===
//         data.campaignSetups?.filter((a) => a.agencyApprovalStatusId && [Status.Approved].includes(a.agencyApprovalStatusId)).length
//     );
// }
export function isAllCampaignSetupSendToAgencyCompleted(data: IBriefDetailsResponse) {
    const totalObjectiveCount = data.spentByObjectives?.reduce((acc, platform) => {
        const activeObjectives = platform.spentByObjectiveAndBudgets?.filter(o => !o.isDeleted).length ?? 0;
        return acc + activeObjectives;
    }, 0) ?? 0;

    const sentOrApprovedCount = data.campaignSetups?.filter(
        (a) =>
            a.agencyApprovalStatusId &&
            [Status.Approved, Status.SentToAgencyForApproval].includes(a.agencyApprovalStatusId),
    ).length ?? 0;

    return totalObjectiveCount > 0 && totalObjectiveCount === sentOrApprovedCount;
}

export function getAllCampaignsForReview(data?: IBriefDetailsResponse) {
    return (
        data?.campaignSetups?.filter((campaignSetups) => {
            return [Status.SubmitForReviewInternally, Status.Approved, Status.Rejected].includes(campaignSetups?.setupStatusId!);
        }) ?? []
    );
}

export function isCampaignReviewCompleted(data?: IBriefDetailsResponse) {
    return (
        data?.campaignSetups &&
        data.campaignSetups.length > 0 &&
        data?.campaignSetups.every((a) => a.setupStatusId && [Status.Approved, Status.Rejected].includes(a.setupStatusId))
    );
}
export const isBriefCompleted = (data: IBriefDetailsResponse) => {
    return (
        (data?.status?.id === Status.CampaignLive || data?.status?.id === Status.CampaignEnded) && data?.formListId === FormListEnum.Brief
    );
};

export const isBriefCreatedBefore20230214 = (data: IBriefDetailsResponse) => {
    return data.dateCreated && new Date(data.dateCreated).getTime() < new Date(2023, 1, 14).getTime();
};
export const isBriefCreatedBefore20260314 = (data: IBriefDetailsResponse) => {
    return data.dateCreated && new Date(data.dateCreated).getTime() < new Date(2026, 2, 14).getTime();
};
export const isDAEmpty = (data: IBriefDetailsResponse) => {
     if (isBriefCreatedBefore20230214(data)) {
        return !data.digitalAssets?.[0];
    }
    return !data.digitalAssets?.length;
};
export const isPOEmpty = (data: IBriefDetailsResponse) => {
    if (!data?.purchaseOrders || undefined) {
        return true;
    } else return false;
};
export const isIMEmpty = (data: IBriefDetailsResponse) => {
    if (!data?.implementationPlans || undefined) {
        return true;
    } else return false;
};
export const isCampSetupEmpty = (data: IBriefDetailsResponse) => {
    if (data?.campaignSetups?.length === 0 || undefined) {
        return true;
    } else return false;
};
export const isPOLEmpty = (data: IBriefDetailsResponse) => {
    if (data?.campaignLiveProof?.length === 0 || undefined) {
        return true;
    } else return false;
};
export const isDigitalAssetCompleted = (data: IBriefDetailsResponse) => {
    // Before Feb 2023: just check first asset
    if (isBriefCreatedBefore20230214(data)) {
        const id = data.digitalAssets?.[0]?.statusId;
        if (id) {
            return [Status.Approved, Status.Completed].includes(id);
        }
        return false;
    }

    //Before March 2026: flat check — all platform assets must be approved/completed
    if (isBriefCreatedBefore20260314(data)) {
        return data.spentByObjectives?.every((o) => {
            const assets = data.digitalAssets?.filter((a) => a.platformId === o.platform.id) ?? [];
            return (
                assets.length > 0 &&
                assets.every((a) => a.statusId && [Status.Approved, Status.Completed].includes(a.statusId))
            );
        });
    }

    //After March 2026: objective-based check
    return data.spentByObjectives?.every((o) => {
        const platformObjectiveIds = o.spentByObjectiveAndBudgets
            ?.map((b) => b.platformObjective?.id)
            .filter(Boolean) ?? [];

        if (platformObjectiveIds.length === 0) return false;

        return platformObjectiveIds.every((objectiveId) => {
            const assets = data.digitalAssets?.filter(
                (a) => a.platformId === o.platform.id && a.pObjectiveId === objectiveId,
            ) ?? [];

            return (
                assets.length > 0 &&
                assets.every(
                    (a) => a.statusId && [Status.Approved, Status.Completed].includes(a.statusId),
                )
            );
        });
    });
};
// export function isAllLiveProofApprovedOrCompleted(data: IBriefDetailsResponse) {
//     let plCount = data.spentByObjectives?.length ?? 0;
//     return plCount === data.campaignLiveProof?.filter((a) => a.statusId && [Status.Approved, Status.Completed].includes(a.statusId)).length;
// }
export function isAllLiveProofApprovedOrCompleted(data: IBriefDetailsResponse) {
    const isCreatedBefore202603 = isBriefCreatedBefore20260314(data);

    if (isCreatedBefore202603) {
        // Old logic: one liveProof per platform
        const plCount = data.spentByObjectives?.length ?? 0;
        return (
            plCount > 0 &&
            plCount ===
                data.campaignLiveProof?.filter(
                    (a) => a.statusId && [Status.Approved, Status.Completed].includes(a.statusId),
                ).length
        );
    }

    // New logic: one liveProof per platform+objective — ALL must be approved/completed
    return (
        (data.spentByObjectives?.length ?? 0) > 0 &&
        data.spentByObjectives?.every((c) =>
            c.spentByObjectiveAndBudgets?.every((obj) => {
                const liveSetup =
                    data.campaignLiveProof?.filter(
                        (a) =>
                            a.platformId === c.platform.id &&
                            a.pObjectiveId === obj.platformObjective?.id,
                    ) ?? [];
                return liveSetup.some(
                    (d) => d?.statusId === Status.Approved || d?.statusId === Status.Completed,
                );
            }),
        )
    );
}
export const isLiveProofUploaded = (data: IBriefDetailsResponse, platformId: number, isCapture?: boolean) => {
    let status = [Status.Approved, Status.Completed, Status.LiveApprovalProof, Status.CaptureLiveProof];
    // if (isCapture) {
    //     status.push(Status.CaptureLiveProof);
    // }
    return (
        (data.campaignLiveProof?.filter((a) => a.statusId && a.platformId === platformId && status.includes(a.statusId)).length ?? 0) > 0
    );
};
export const isObjectiveLiveProofUploaded = (
    data: IBriefDetailsResponse,
    platformId: number,
    isCapture?: boolean,
    pObjectiveId?: number,
) => {
    let status = [Status.Approved, Status.Completed, Status.LiveApprovalProof];
    if (isCapture) {
        status.push(Status.CaptureLiveProof);
    }
    return (
        (data.campaignLiveProof?.filter(
            (a) => a.statusId && a.platformId === platformId && a.pObjectiveId === pObjectiveId && status.includes(a.statusId),
        ).length ?? 0) > 0
    );
};

export const isImplementationPlanCompleted = (data: IBriefDetailsResponse) => {
    return data.implementationPlans?.statusId === Status.Completed || data.implementationPlans?.statusId === Status.Approved;
    // return (
    //     (data.implementationPlans?.implementationPlanItems.filter(
    //         (a) => a.statusId && [Status.Approved, Status.Completed].includes(a.statusId)
    //     ).length ?? 0) > 0
    // );
};
export const anyLiveProofApproved = (data: IBriefDetailsResponse) => {
    return data.campaignSetups?.some((c) => c.platformId && isLiveProofApproved(data, c.platformId));
};
export const isLiveProofApproved = (data: IBriefDetailsResponse, platformId: number) => {
    return (
        (data.campaignLiveProof?.filter(
            (a) => a.statusId && a.platformId === platformId && [Status.Approved, Status.Completed].includes(a.statusId),
        ).length ?? 0) > 0
    );
};
// export function isAllLiveProofSentOrApproved(data: IBriefDetailsResponse) {
//     let plCount = data.spentByObjectives?.length ?? 0;
//     return plCount === data.campaignLiveProof?.filter((a) => a.statusId && [Status.Approved, Status.Completed].includes(a.statusId)).length;
// }
export function isAllLiveProofSentOrApproved(data: IBriefDetailsResponse) {
     console.log("campaignLiveProof entries:", 
        data.campaignLiveProof?.map(a => ({
            platformId: a.platformId,
            pObjectiveId: a.pObjectiveId,
            statusId: a.statusId,
            statusDescription: a.status.description
        }))
    );
    console.log("spentByObjectives:", 
        data.spentByObjectives?.map(c => ({
            platformId: c.platform.id,
            objectives: c.spentByObjectiveAndBudgets?.map(o => ({
                id: o.platformObjective?.id,
                isDeleted: o.isDeleted
            }))
        }))
    );
    const isCreatedBefore202603 = isBriefCreatedBefore20260314(data);

    if (isCreatedBefore202603) {
        // Old logic: one liveProof per platform
        const plCount = data.spentByObjectives?.length ?? 0;
        return (
            plCount > 0 &&
            plCount ===
                data.campaignLiveProof?.filter(
                    (a) =>
                        a.statusId &&
                        [
                            Status.LiveApprovalProof,
                            Status.CaptureLiveProof,
                            Status.Approved,
                            Status.Completed,
                        ].includes(a.statusId),
                ).length
        );
    }

    // New logic: every platform → every objective must have a submitted/approved proof
    return (
        (data.spentByObjectives?.length ?? 0) > 0 &&
        data.spentByObjectives?.every((c) =>
            c.spentByObjectiveAndBudgets
                ?.filter((o) => !o.isDeleted)
                .every((obj) => {
                    const proofs =
                        data.campaignLiveProof?.filter(
                            (a) =>
                                a.platformId === c.platform.id &&
                                a.pObjectiveId === obj.platformObjective?.id,
                        ) ?? [];
                    return proofs.some(
                        (d) =>
                            d?.statusId === Status.LiveApprovalProof ||
                            d?.statusId === Status.CaptureLiveProof ||
                             d?.statusId === Status.Approved ||
                        d?.statusId === Status.Completed,
                           
                    );
                }),
        )
    );
}
// export function isNewPOL(data: IBriefDetailsResponse) {
//     let setupCount =
//         (data?.campaignSetups?.filter((a) => a.statusId && [Status.Approved, Status.Completed].includes(a.statusId)).length ?? 0) > 0;
//     if (setupCount) {
//         setupCount = false; //checkinig status for 'tab color'
//     } else {
//         setupCount = true;
//     }
//     return setupCount;
// }
export function isNewPOL(data: IBriefDetailsResponse) {
    return !data?.campaignLiveProof || data.campaignLiveProof.length === 0;
}
export const isImplPlanItemApproved = (data: IBriefDetailsResponse, platformId: number) => {
    return (
        (data.implementationPlans?.implementationPlanItems?.filter(
            (a) => a.statusId && a.platformId === platformId && [Status.Approved, Status.Completed].includes(a.statusId),
        ).length ?? 0) > 0
    );
};
export const isImplPlanUploaded = (data: IBriefDetailsResponse, platformId: number) => {
    return (
        (data.implementationPlans?.implementationPlanItems?.filter(
            (a) => a.statusId && a.platformId === platformId && [Status.Uploaded, !Status.Rejected].includes(a.statusId),
        ).length ?? 0) > 0
    );
};
// export const isCMS360Completed = (data: IBriefDetailsResponse) => {
//     if (data?.campaignTaggings?.length === 0 || undefined) {
//         return true;
//     } else return false;
// };
// All objectives have at least one tag/IAS image uploaded
// export const isCMS360Completed = (data: IBriefDetailsResponse): boolean => {
//     if (!data?.spentByObjectives?.length) return false;

//     return data.spentByObjectives.every((o) => {
//         const platformObjectiveIds = o.spentByObjectiveAndBudgets
//             ?.map((b) => b.platformObjective?.id)
//             .filter(Boolean) ?? [];

//         if (platformObjectiveIds.length === 0) return false;

//         return platformObjectiveIds.every((objectiveId) => {
//             const tagging = data.campaignTaggings?.find(
//                 (t) => t.platformId === o.platform.id && t.pObjectiveId === objectiveId,
//             );
//             if (!tagging) return false;

//             return ((tagging.campaignTaggingImages?.length ?? 0) > 0) || 
//        ((tagging.campaignIasImages?.length ?? 0) > 0);
//         });
//     });
// };

// export const isCMS360Empty = (data: IBriefDetailsResponse): boolean => {
//     if (!data?.campaignTaggings?.length) return true;

//     return data.campaignTaggings.every(
//         (t) =>
//             (t.campaignTaggingImages?.length ?? 0) === 0 &&
//             (t.campaignIasImages?.length ?? 0) === 0,
//     );
// };
export const isCMS360Completed = (data: IBriefDetailsResponse): boolean => {
    if (!data?.spentByObjectives?.length) return false;
    const isLegacy = isBriefCreatedBefore20260314(data);

    return data.spentByObjectives.every((o) => {
        const platformObjectiveIds = o.spentByObjectiveAndBudgets
            ?.map((b) => b.platformObjective?.id)
            .filter(Boolean) ?? [];

        if (platformObjectiveIds.length === 0) return false;

        return platformObjectiveIds.every((objectiveId) => {
            const tagging = data.campaignTaggings?.find(
                (t) => t.platformId === o.platform.id && t.pObjectiveId === objectiveId,
            );
            if (!tagging) return false;

            // legacy briefs have no statusId, fall back to image count
            if (isLegacy) {
                return ((tagging.campaignTaggingImages?.length ?? 0) > 0) ||
                       ((tagging.campaignIasImages?.length ?? 0) > 0);
            }

            return tagging.statusId === Status.Completed;
        });
    });
};

export const isCMS360Empty = (data: IBriefDetailsResponse): boolean => {
    if (!data?.campaignTaggings?.length) return true;
    const isLegacy = isBriefCreatedBefore20260314(data);

    return data.campaignTaggings.every((t) => {
        if (isLegacy) {
            return (t.campaignTaggingImages?.length ?? 0) === 0 &&
                   (t.campaignIasImages?.length ?? 0) === 0;
        }

        return (t.campaignTaggingImages?.length ?? 0) === 0 &&
               (t.campaignIasImages?.length ?? 0) === 0 &&
               (!t.statusId || t.statusId === Status.Empty);  
    });
};
export const isCMS360LegacyCompleted = (data: IBriefDetailsResponse): boolean => {
    if (!data?.campaignTaggings?.length) return false;
    return data.campaignTaggings.every(
        (t) =>
            ((t.campaignTaggingImages?.length ?? 0) > 0) ||
            ((t.campaignIasImages?.length ?? 0) > 0),
    );
};

export const isCMS360LegacyEmpty = (data: IBriefDetailsResponse): boolean => {
    if (!data?.campaignTaggings?.length) return true;
    return data.campaignTaggings.every(
        (t) =>
            (t.campaignTaggingImages?.length ?? 0) === 0 &&
            (t.campaignIasImages?.length ?? 0) === 0,
    );
};